/**
 * Mirrors the backend's stuck-QUEUED rule (RFC 0002 §2 Fase 2 —
 * `Notification.isStuckInQueue` / `RetryNotificationUseCase`,
 * `DEFAULT_STUCK_QUEUE_THRESHOLD_MS` in `retry-policy.ts`): a QUEUED
 * notification with zero delivery attempts that has sat for more than 10
 * minutes never got a job — the outbox enqueue for it never ran
 * (bead noten-zre). It is retryable exactly like FAILED, and the UI calls
 * it out so an operator doesn't read "QUEUED" as "in progress".
 *
 * Kept in sync manually with the backend constant — there is no shared
 * package between backend and backoffice for this value.
 */
export const STUCK_QUEUE_THRESHOLD_MS = 10 * 60_000;

export interface RetryableNotification {
  status: string;
  attemptsCount: number;
  createdAt: string;
}

export function isStuckInQueue(
  notification: RetryableNotification,
  now: Date = new Date(),
): boolean {
  return (
    notification.status === 'QUEUED' &&
    notification.attemptsCount === 0 &&
    now.getTime() - new Date(notification.createdAt).getTime() > STUCK_QUEUE_THRESHOLD_MS
  );
}

/** FAILED, or a QUEUED notification stuck without a delivery attempt — the two states `POST /notifications/:id/retry` accepts. */
export function isRetryable(notification: RetryableNotification, now: Date = new Date()): boolean {
  return notification.status === 'FAILED' || isStuckInQueue(notification, now);
}

export function minutesStuck(createdAt: string, now: Date = new Date()): number {
  return Math.floor((now.getTime() - new Date(createdAt).getTime()) / 60_000);
}
